
// @flow
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { includes } from 'ramda';

import {
  GET_CAFETERIA_CUSTOMER_COMPLEXES_FREE,
  convertParams,
  convertResult,
} from 'app/common/api/requests/cafeteria/customer/orders/complexes/free';

import { api } from 'app/api';
import type { Complex, ComplexMap } from 'app/common/components/Cafeteria/dataTypes/order/Complex.types';
import usePopupErr from 'app/common/hooks/usePopupErr';
import SimpleDate from 'app/common/classes/simple-date';


type Options = {
  customerId: string,
  date: SimpleDate,
  orderRequestId: string,
  onStartRequest: (itemsMap: ComplexMap) => void | Promise<void>,
  onFinishRequest: (itemsMap: ComplexMap) => void | Promise<void>,
}

export default function useComplexesFreeRequest(options: Options): [
  (itemsMap: ComplexMap) => Promise<boolean>,
  boolean
] {
  const { t } = useTranslation('cafeteria');
  const [popupErr] = usePopupErr();

  const { date, customerId, orderRequestId, onStartRequest, onFinishRequest } = options;
  const [loading, setLoading] = useState(false);

  const request = useCallback(async (complexesMap: ComplexMap) => {
    onStartRequest(complexesMap);
    setLoading(true);

    const complexesItems: Array<Complex> = Array.from(complexesMap.values());
    const res = await Promise.all(complexesItems.map(({ id }) => (
      api.request(GET_CAFETERIA_CUSTOMER_COMPLEXES_FREE, {
        params: {
          id,
          date,
          customerId,
          orderRequestId,
        },
        convertions: {
          convertParams,
          convertResult,
        },
      })
    )));

    if (includes(null, res)) {
      popupErr(t(
        'order_request_sales_put_on_food_free_complects_fetch_error',
        'Не удалось получить данные о льготных комплектах',
      ));
      return false;
    }

    onFinishRequest(new Map<string, Complex>(
      complexesItems.map((complex, index) => {
        return [complex.id, {
          ...complex,
          freeCount: res[index] ? 1 : 0,
        }];
      }),
    ));

    setLoading(false);
    return true;
  }, [date, customerId, orderRequestId, onStartRequest, onFinishRequest, popupErr, t]);

  return [request, loading];
}
